/**
 * @file src/local/tags/localCovers.js
 * @description The cover a file carried, made small enough to keep.
 *
 * ## Why anything happens to it at all
 *
 * A cover embedded in an mp3 is routinely 1500 square and several hundred kilobytes, because it
 * was made for a record sleeve rather than for a card on a rail. Kept as it came, a folder of five
 * hundred tagged files would put a couple of hundred megabytes of album art on somebody's phone —
 * beside the audio, in the same quota the offline collections share, and counted in the number the
 * local files page reports as "on this machine".
 *
 * So a cover is decoded straight to the size a card actually draws and re-encoded before it is
 * ever stored. That turns a few hundred kilobytes into a few tens, and it is the whole answer to
 * "what happens on a phone importing a large folder": the ceiling is per file and it is small.
 *
 * The decode itself is the browser's own, off the main thread, and one file at a time — imports
 * are serialised through the analysis queue, so there is never a folder's worth of bitmaps at once.
 *
 * ## When nothing happens to it
 *
 * A browser without `OffscreenCanvas` cannot re-encode, so a cover that is already small is stored
 * as it came and one that is not is dropped. A card with a letter on it is not a failure; a phone
 * quietly holding two hundred megabytes of album art would be.
 */

/** The square a stored cover is fitted into. A rail card draws far smaller than this. */
export const COVER_SIZE = 512;

/** Above this, a cover is not worth keeping as it came on a browser that cannot re-encode it. */
const KEEP_AS_IS_BYTES = 96 * 1024;

/** WebP where it is understood, JPEG where it is not — both far below PNG for a photograph. */
const ENCODINGS = ['image/webp', 'image/jpeg'];

function canRescale() {
  return (
    typeof createImageBitmap === 'function' &&
    typeof OffscreenCanvas !== 'undefined' &&
    typeof OffscreenCanvas.prototype?.convertToBlob === 'function'
  );
}

/**
 * Fit a cover into `COVER_SIZE` and re-encode it, or hand back something storable without.
 *
 * Never throws: a cover that will not decode is a file with no cover, which is the state almost
 * every file is in anyway.
 *
 * @param {{ mimeType: string, bytes: Uint8Array }|null} picture as `readAudioTags` returns it
 * @returns {Promise<Blob|null>} the bytes to store, or null to store none
 */
export async function prepareLocalCover(picture) {
  if (!picture?.bytes?.length) return null;
  const source = new Blob([picture.bytes], { type: picture.mimeType || 'image/jpeg' });
  if (!canRescale()) {
    // Nothing here can shrink it. Keep a small one, drop a large one — never spend a phone's disk
    // on a picture this browser could not have made smaller.
    return source.size <= KEEP_AS_IS_BYTES ? source : null;
  }
  let bitmap;
  try {
    bitmap = await createImageBitmap(source);
  } catch (error) {
    console.warn('[local] a cover could not be decoded', error);
    return null;
  }
  try {
    // Fitted, not squared. Almost every cover is square and this changes nothing for those; the
    // occasional sleeve that is not would otherwise be stretched, which is worse than smaller.
    const scale = Math.min(1, COVER_SIZE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = new OffscreenCanvas(width, height);
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(bitmap, 0, 0, width, height);
    for (const type of ENCODINGS) {
      try {
        const blob = await canvas.convertToBlob({ type, quality: 0.82 });
        // A browser that does not understand the type answers in its own instead of failing, so
        // the answer is checked rather than trusted.
        if (blob?.size > 0 && blob.type === type) return blob;
      } catch {
        // Try the next one; a browser that encodes neither falls through to null.
      }
    }
    return null;
  } catch (error) {
    console.warn('[local] a cover could not be re-encoded', error);
    return null;
  } finally {
    bitmap.close?.();
  }
}
